import { getSavedView } from "@/features/saved-views/saved-views-client";
import { generateViewerUrl } from "@/utils/redirects";
import {
  isSavedViewData,
  type SavedViewData,
} from "@faro-lotv/sphere-viewer-contracts";
import { isPlainObject } from "es-toolkit";

/** The navigation target derived from a `/share/<id>` link. */
export type ResolvedShareLink = {
  /** The relative viewer URL to navigate to. */
  url: string;

  /**
   * The saved view state to apply. It is meant to be passed via the router
   * location state rather than the URL, so the payload is not limited by the
   * browser URL length and never appears in the address bar.
   */
  savedView: SavedViewData;
};

/**
 * Resolves a `/share/<id>` link into a viewer navigation target by loading the
 * saved view from the backend.
 *
 * The payload needs no validation here: `getSavedView` already rejects a response
 * whose `data` does not match the contract. Narrowing it into a `ValidSavedView`
 * is the resolver's job, since only that can check the view against the project.
 *
 * @param shareId The short, URL-safe id from a `/share/<id>` link.
 * @returns the clean viewer URL and the structured saved view to apply.
 * @throws if the saved view could not be loaded or is malformed.
 */
export async function resolveShareLink(
  shareId: string,
): Promise<ResolvedShareLink> {
  const savedView = await getSavedView(shareId);
  return {
    // The project id comes from a record anyone can create through the anonymous
    // api, where it is only length-checked, so it is encoded here: a `?`, `#` or
    // `/` would otherwise change the navigation target instead of staying a
    // single path segment. A real project id is a GUID, which encoding leaves
    // untouched.
    url: generateViewerUrl(encodeURIComponent(savedView.projectId)),
    savedView: savedView.data,
  };
}

/** The router location state a `/share/<id>` redirect hands to the viewer. */
export type SavedViewLocationState = {
  /** The saved view the viewer should open. */
  savedView: SavedViewData;
};

/**
 * Reads the saved view a share link redirect passed through the router.
 *
 * The location state is not a trusted channel: it survives in the browser's
 * history, so it can be stale from an older app version or hand-crafted. Running
 * it through the contract's own guard means a payload the viewer cannot use is
 * treated as "no view", exactly like a url with no link in it.
 *
 * @param state The `location.state` of the current router location.
 * @returns the saved view to open, if the location carries a usable one.
 */
export function readSavedViewLocationState(
  state: unknown,
): SavedViewData | undefined {
  if (!isPlainObject(state)) return undefined;
  const { savedView } = state;
  return isSavedViewData(savedView) ? savedView : undefined;
}
